import DOMPurify from "dompurify";
import { Variable } from "../../models/interactions/Variable";
import {
  tokenizeVariableString,
  VariableTokenIssue,
  VariableTokenOptions,
} from "../variables/VariableTokenUtils";

/**
 * Canonical rich text format stored in localized string values.
 *
 * This is the contract with the training player (Unity/TextMeshPro side):
 * - Inline styles: <b>, <i>, <u>, <s> (nestable)
 * - Font size: <span style="font-size:20px"> — exactly one property, px units,
 *   values limited to FONT_SIZE_PRESETS. The default size stores no tag.
 * - Color: <span style="color:#rrggbb"> — exactly one property, lowercase
 *   6-digit hex.
 * - Newlines: literal "\n" (never <br> or <div> in storage)
 * - Variable tokens: existing [$GV_<id>] syntax, unchanged
 * - Text escaping: &amp; &lt; &gt;
 * - Nesting order (outer to inner): size span, color span, <b>, <i>, <u>, <s>
 *
 * Legacy plain-text values (no formatting tags) round-trip unchanged.
 */

export const FONT_SIZE_PRESETS = [12, 15, 20, 28] as const;
export const DEFAULT_FONT_SIZE_LABEL = "Default";

export const VARIABLE_CHIP_ATTR = "data-variable-token";
export const VARIABLE_CHIP_CLASS = "rich-text-variable-chip";
export const UNKNOWN_TOKEN_CLASS = "rich-text-unknown-token";

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["b", "i", "u", "s", "em", "strong", "strike", "del", "span", "font", "br", "div", "p"],
  ALLOWED_ATTR: ["style", "size", "color", "class", "contenteditable", VARIABLE_CHIP_ATTR],
};

const FORMATTING_TAG_REGEX = /<\/?(?:b|i|u|s|em|strong|strike|del|span|font|br|div|p)\b[^>]*\/?>/gi;
const HAS_FORMATTING_REGEX = /<\/?(?:b|i|u|s|span)\b[^>]*>/i;

// font element legacy size attribute (1-7) to approximate px values.
const FONT_ATTR_SIZE_TO_PX = [0, 10, 13, 16, 18, 24, 32, 48];

const BLOCK_TAGS = new Set(["DIV", "P"]);

export function sanitizeRichTextHtml(html: string): string {
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
}

export function hasFormattingTags(value: string): boolean {
  return HAS_FORMATTING_REGEX.test(value);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function unescapeHtml(text: string): string {
  return text
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&");
}

/**
 * Removes formatting tags and unescapes entities, keeping [$...] tokens and
 * newlines. Shared by search matching, validation emptiness checks, and the
 * plain-text node preview fields.
 */
export function stripFormattingTags(value: string): string {
  if (!value) return value;
  return unescapeHtml(value.replace(FORMATTING_TAG_REGEX, ""));
}

export function snapToFontSizePreset(sizePx: number): number {
  let closest: number = FONT_SIZE_PRESETS[0];
  for (const preset of FONT_SIZE_PRESETS) {
    if (Math.abs(preset - sizePx) < Math.abs(closest - sizePx)) {
      closest = preset;
    }
  }
  return closest;
}

function normalizeHexColor(color: string): string | null {
  const trimmed = color.trim().toLowerCase();

  const hexMatch = trimmed.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hexMatch) {
    const hex = hexMatch[1];
    if (hex.length === 3) {
      return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
    }
    return `#${hex}`;
  }

  const rgbMatch = trimmed.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    const toHex = (channel: string) =>
      Math.min(255, parseInt(channel, 10)).toString(16).padStart(2, "0");
    return `#${toHex(rgbMatch[1])}${toHex(rgbMatch[2])}${toHex(rgbMatch[3])}`;
  }

  return null;
}

interface RunFormat {
  sizePx: number | null;
  colorHex: string | null;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
}

const PLAIN_FORMAT: RunFormat = {
  sizePx: null,
  colorHex: null,
  bold: false,
  italic: false,
  underline: false,
  strike: false,
};

interface Run {
  // Escaped text ready for output, or a raw [$...] token.
  text: string;
  isToken: boolean;
  format: RunFormat;
}

function getElementFormat(element: HTMLElement, inherited: RunFormat): RunFormat {
  const format = { ...inherited };
  const tag = element.tagName;

  if (tag === "B" || tag === "STRONG") format.bold = true;
  if (tag === "I" || tag === "EM") format.italic = true;
  if (tag === "U") format.underline = true;
  if (tag === "S" || tag === "STRIKE" || tag === "DEL") format.strike = true;

  if (tag === "FONT") {
    const sizeAttr = parseInt(element.getAttribute("size") ?? "", 10);
    if (sizeAttr >= 1 && sizeAttr <= 7) {
      format.sizePx = snapToFontSizePreset(FONT_ATTR_SIZE_TO_PX[sizeAttr]);
    }
    const colorAttr = element.getAttribute("color");
    if (colorAttr) {
      format.colorHex = normalizeHexColor(colorAttr) ?? format.colorHex;
    }
  }

  const style = element.style;
  if (style) {
    const fontWeight = style.fontWeight;
    if (fontWeight === "bold" || parseInt(fontWeight, 10) >= 600) format.bold = true;
    if (fontWeight === "normal" || (parseInt(fontWeight, 10) > 0 && parseInt(fontWeight, 10) < 600)) {
      format.bold = false;
    }

    if (style.fontStyle === "italic") format.italic = true;
    if (style.fontStyle === "normal") format.italic = false;

    const decoration = `${style.textDecoration} ${style.textDecorationLine ?? ""}`;
    if (decoration.includes("underline")) format.underline = true;
    if (decoration.includes("line-through")) format.strike = true;

    const fontSize = style.fontSize;
    const sizeMatch = fontSize?.match(/^(\d+(?:\.\d+)?)px$/);
    if (sizeMatch) {
      format.sizePx = snapToFontSizePreset(parseFloat(sizeMatch[1]));
    }

    if (style.color) {
      format.colorHex = normalizeHexColor(style.color) ?? format.colorHex;
    }
  }

  return format;
}

interface CollectContext {
  variables: Variable[];
  allowVariables: boolean;
  checkVariables: boolean;
  issues: VariableTokenIssue[];
}

function pushTextRuns(text: string, format: RunFormat, runs: Run[], ctx: CollectContext) {
  if (!ctx.allowVariables) {
    runs.push({ text: escapeHtml(text), isToken: false, format });
    return;
  }

  // Token boundaries always split runs, so serialization does not depend on
  // how the browser happens to split text nodes around variable chips.
  tokenizeVariableString(text, ctx.variables).forEach((segment) => {
    if (segment.type === "text") {
      runs.push({ text: escapeHtml(segment.text), isToken: false, format });
      return;
    }

    if (segment.source === "name" && segment.isKnown && segment.variable) {
      const id = segment.variable.id;
      const tokenBody = id.startsWith("GV_") ? id : `GV_${id}`;
      runs.push({ text: `[$${tokenBody}]`, isToken: true, format });
      return;
    }

    if (!segment.isKnown && ctx.checkVariables) {
      ctx.issues.push({
        type: segment.source === "id" ? "unknown_id" : "unknown_name",
        tokenBody: segment.tokenBody!,
        tokenText: segment.text,
      });
    }

    runs.push({ text: segment.text, isToken: true, format });
  });
}

function collectRuns(node: Node, format: RunFormat, runs: Run[], ctx: CollectContext) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent ?? "").replace(/\u200B/g, "");
    if (text.length === 0) return;
    pushTextRuns(text, format, runs, ctx);
    return;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const element = node as HTMLElement;

  if (element.tagName === "BR") {
    runs.push({ text: "\n", isToken: false, format });
    return;
  }

  if (element.hasAttribute(VARIABLE_CHIP_ATTR)) {
    const tokenBody = element.getAttribute(VARIABLE_CHIP_ATTR);
    if (tokenBody) {
      runs.push({ text: `[$${tokenBody}]`, isToken: true, format });
    }
    return;
  }

  if (BLOCK_TAGS.has(element.tagName)) {
    const lastRun = runs[runs.length - 1];
    if (runs.length > 0 && !(lastRun && lastRun.text.endsWith("\n"))) {
      runs.push({ text: "\n", isToken: false, format: PLAIN_FORMAT });
    }
  }

  const childFormat = getElementFormat(element, format);
  element.childNodes.forEach((child) => collectRuns(child, childFormat, runs, ctx));
}

// Ordered outer-to-inner; determines canonical nesting.
function formatToStack(format: RunFormat): string[] {
  const stack: string[] = [];
  if (format.sizePx !== null) stack.push(`<span style="font-size:${format.sizePx}px">`);
  if (format.colorHex !== null) stack.push(`<span style="color:${format.colorHex}">`);
  if (format.bold) stack.push("<b>");
  if (format.italic) stack.push("<i>");
  if (format.underline) stack.push("<u>");
  if (format.strike) stack.push("<s>");
  return stack;
}

function closingTagFor(openingTag: string): string {
  if (openingTag.startsWith("<span")) return "</span>";
  return openingTag.replace("<", "</");
}

function runsToCanonicalHtml(runs: Run[]): string {
  let output = "";
  let openStack: string[] = [];

  runs.forEach((run) => {
    // Whitespace-only runs (newlines, stray spaces) never open or close
    // formatting tags of their own, so bold text spanning a line break stays
    // inside one <b> instead of splitting into <b>a</b>\n<b>b</b>.
    const targetStack =
      run.text.trim().length === 0 && !run.isToken
        ? openStack
        : formatToStack(run.format);

    let commonDepth = 0;
    while (
      commonDepth < openStack.length &&
      commonDepth < targetStack.length &&
      openStack[commonDepth] === targetStack[commonDepth]
    ) {
      commonDepth++;
    }

    for (let i = openStack.length - 1; i >= commonDepth; i--) {
      output += closingTagFor(openStack[i]);
    }
    for (let i = commonDepth; i < targetStack.length; i++) {
      output += targetStack[i];
    }

    openStack = targetStack;
    output += run.text;
  });

  for (let i = openStack.length - 1; i >= 0; i--) {
    output += closingTagFor(openStack[i]);
  }

  return output;
}

export interface RichTextCanonicalResult {
  value: string;
  issues: VariableTokenIssue[];
}

/**
 * Serializes the live contentEditable DOM to the canonical stored format.
 * Handles messy browser output (strong/em, style-based formatting, font
 * elements from execCommand, div/br line breaks) and converts variable chip
 * spans back to [$GV_<id>] tokens. Manually typed [$Name] tokens are
 * converted to stored id form, mirroring the plain textarea behavior.
 */
export function editorDomToCanonical(
  root: HTMLElement,
  variables: Variable[],
  options?: VariableTokenOptions
): RichTextCanonicalResult {
  const runs: Run[] = [];
  const ctx: CollectContext = {
    variables,
    allowVariables: options?.allowVariables ?? true,
    checkVariables: options?.checkVariables ?? true,
    issues: [],
  };
  root.childNodes.forEach((child) => collectRuns(child, PLAIN_FORMAT, runs, ctx));

  // A single trailing <br> in a contentEditable is a caret placeholder, not
  // content; collapse an entirely-empty result to "".
  const canonical = runsToCanonicalHtml(runs);
  if (canonical === "\n") {
    return { value: "", issues: [] };
  }

  return { value: canonical, issues: ctx.issues };
}

export interface RichTextEditorHtmlResult {
  html: string;
  issues: VariableTokenIssue[];
}

export function getStoredTokenBody(variable: Variable): string {
  return variable.id.startsWith("GV_") ? variable.id : `GV_${variable.id}`;
}

/** Builds the non-editable chip element used inside the rich text editor. */
export function createVariableChipElement(variable: Variable): HTMLSpanElement {
  const chip = document.createElement("span");
  chip.setAttribute("contenteditable", "false");
  chip.className = VARIABLE_CHIP_CLASS;
  chip.setAttribute(VARIABLE_CHIP_ATTR, getStoredTokenBody(variable));
  chip.textContent = variable.name;
  return chip;
}

function buildChipHtml(tokenBody: string, label: string): string {
  return (
    `<span contenteditable="false" class="${VARIABLE_CHIP_CLASS}" ` +
    `${VARIABLE_CHIP_ATTR}="${escapeHtml(tokenBody)}">${escapeHtml(label)}</span>`
  );
}

function textToEditorHtml(
  text: string,
  variables: Variable[],
  options: VariableTokenOptions | undefined,
  issues: VariableTokenIssue[]
): string {
  const allowVariables = options?.allowVariables ?? true;
  const checkVariables = options?.checkVariables ?? true;

  const withBreaks = (escaped: string) => escaped.replace(/\n/g, "<br>");

  if (!allowVariables) {
    return withBreaks(text);
  }

  // The incoming text is already escaped (it comes from sanitized HTML or the
  // plain-text escape path), so tokenization runs on the escaped form.
  const segments = tokenizeVariableString(text, variables);

  return segments
    .map((segment) => {
      if (segment.type === "token" && segment.isKnown && segment.variable) {
        return buildChipHtml(getStoredTokenBody(segment.variable), segment.variable.name);
      }

      if (segment.type === "token" && !segment.isKnown && checkVariables) {
        issues.push({
          type: segment.source === "id" ? "unknown_id" : "unknown_name",
          tokenBody: segment.tokenBody!,
          tokenText: segment.text,
        });
        return `<span class="${UNKNOWN_TOKEN_CLASS}">${withBreaks(segment.text)}</span>`;
      }

      return withBreaks(segment.text);
    })
    .join("");
}

/**
 * Converts a stored canonical value to HTML for the editor's innerHTML:
 * sanitized, with known [$...] tokens replaced by non-editable chip spans and
 * newlines rendered as <br>. Legacy plain-text values (no formatting tags)
 * are escaped verbatim so stray "<" or "&" characters survive.
 */
export function richTextToEditorHtml(
  stored: string,
  variables: Variable[],
  options?: VariableTokenOptions
): RichTextEditorHtmlResult {
  const issues: VariableTokenIssue[] = [];

  if (!stored) {
    return { html: "", issues };
  }

  if (!hasFormattingTags(stored)) {
    return {
      html: textToEditorHtml(escapeHtml(stored), variables, options, issues),
      issues,
    };
  }

  const template = document.createElement("template");
  template.innerHTML = sanitizeRichTextHtml(stored);

  const walkTextNodes = (node: Node) => {
    const children = Array.from(node.childNodes);
    children.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ?? "";
        const replacementHtml = textToEditorHtml(escapeHtml(text), variables, options, issues);
        const fragmentTemplate = document.createElement("template");
        fragmentTemplate.innerHTML = replacementHtml;
        node.replaceChild(fragmentTemplate.content, child);
      } else {
        walkTextNodes(child);
      }
    });
  };

  walkTextNodes(template.content);

  return { html: template.innerHTML, issues };
}
